/**
 * PortalLayout — Layout wrapper for authenticated portal pages
 *
 * Uses Sidebar + Outlet pattern for nested routes.
 * Also exports the legacy PortalLayout for backward compatibility.
 *
 * Below the `md` breakpoint (< 768px) the layout switches to a
 * mobile pattern (PR (b2)):
 *   - The sidebar is hidden by default and slides in from the left
 *     as a 280px-wide drawer when the hamburger in `MobileHeader`
 *     is tapped.
 *   - A backdrop dims the main content while the drawer is open.
 *     Tapping the backdrop closes the drawer.
 *   - The drawer auto-closes on route change so navigation
 *     dismisses it.
 *
 * Requirements: 3.1, 8.1, 9.1, 10.1, 10.4, 14.9 (mobile sidebar UX)
 */

import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { MobileHeader } from './MobileHeader';
import { useIsBelowMd } from '@/lib/useMediaQuery';
import { NotificationBell } from '@/components/ui/NotificationBell';

export interface PortalLayoutProps {
  children?: React.ReactNode;
  /** Active SideNav item identifier (legacy prop) */
  activeNavItem?: string;
}

export function PortalLayout({ children }: PortalLayoutProps) {
  const location = useLocation();
  const isMessagesPage = location.pathname.includes('/messages');
  const isBelowMd = useIsBelowMd();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  return (
    <div data-testid="portal-layout" className="flex h-screen bg-background text-foreground overflow-hidden flex-col md:flex-row">
      {isBelowMd && (
        <MobileHeader onOpenSidebar={() => setMobileSidebarOpen(true)} />
      )}
      {isBelowMd && mobileSidebarOpen && (
        <div
          data-testid="portal-layout-backdrop"
          className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <Sidebar
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      <main
        data-testid="portal-layout-content"
        className="flex-grow p-4 sm:p-8 md:p-12 lg:p-20 overflow-y-auto overflow-x-hidden has-[[data-fullbleed=true]]:p-0 has-[[data-fullbleed=true]]:overflow-hidden relative min-w-0"
      >
        {!isMessagesPage && !isBelowMd && (
          <div className="absolute top-4 right-4 z-50">
            <NotificationBell />
          </div>
        )}
        {children || <Outlet />}
      </main>
    </div>
  );
}
